import { NavLink, useLocation } from "react-router-dom";
import PanelShell from "./PanelShell";
import AreaGuard from "../routes/AreaGuard";
import { useAuthContext } from "../context/AuthProvider";
import { can, canEnterAdmin, canEnterPanel } from "../utils/access";

/** Rótulo de la barra superior según la sección abierta. */
const pageTitleFor = (pathname) => {
    if (pathname.startsWith('/recharge')) return 'Recargar tokens';
    if (/^\/live\/.+/.test(pathname)) return 'En vivo';
    // El mismo nombre que el título de la pantalla y que la entrada del menú.
    return 'Salas en vivo';
};

/**
 * Consumo del contenido: las salas y la recarga.
 *
 * Es el área a la que llega cualquiera con sesión, así que enseña además el
 * atajo al área de quien tiene otra: la modelo a su panel, quien administra al
 * suyo. Sin eso había que volver a la portada para cambiar de sitio.
 */
export default function StreamingLayout() {
    const { user, balance, access } = useAuthContext();
    const { pathname } = useLocation();

    const sections = [
        {
            title: 'Transmisiones',
            items: [
                { to: '/live', end: true, icon: 'fa-tower-broadcast', label: 'Salas en vivo' },
                // La modelo no recarga tokens: no se le ofrece la pantalla que
                // el backend le va a negar.
                can(user, 'recharge_tokens') && { to: '/recharge', icon: 'fa-coins', label: 'Recargar' },
            ],
        },
        {
            title: 'Ir al sitio',
            items: [
                { to: '/modelos', icon: 'fa-id-badge', label: 'Catálogo' },
                { to: '/', icon: 'fa-house', label: 'Portada' },
            ],
        },
        {
            title: 'Mi cuenta',
            items: [
                canEnterAdmin(user) && { to: '/admin', icon: 'fa-shield-halved', label: 'Administración' },
                canEnterPanel(user) && { to: '/panel', icon: 'fa-id-card', label: 'Mi perfil' },
            ],
        },
    ];

    const actions = (
        <>
            {/* Días restantes de la ventana: el usuario debe poder verlos sin
                entrar a la pantalla de recarga. */}
            {access?.subscription_active && !access?.is_exempt && (
                <span
                    className="subscription-pill d-none d-sm-inline-flex"
                    title={`Tu acceso vence el ${new Date(access.subscription_expires_at).toLocaleDateString()}`}
                >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
                    {access.days_remaining} {access.days_remaining === 1 ? 'día' : 'días'}
                </span>
            )}

            {/* El saldo se ocultaba entero por debajo de 576px: en el móvil no
                había forma de saber cuántos tokens quedaban. Ahora se encoge. */}
            {can(user, 'recharge_tokens') && (
                <NavLink to="/recharge" className="token-balance-display" title="Ver planes y recargar tokens">
                    <i className="fa-solid fa-coins text-warning" aria-hidden="true"></i>
                    <span className="balance-value">{balance ?? '...'}</span>
                    <span className="balance-label">Tokens</span>
                </NavLink>
            )}
        </>
    );

    // Basta con tener sesión: es el área a la que llega todo el mundo.
    return (
        <AreaGuard allowed={(usuario) => Boolean(usuario)}>
            <PanelShell sections={sections} title={pageTitleFor(pathname)} actions={actions} />
        </AreaGuard>
    );
}
